import { ReportItemSchema, ReportSchema } from '@retro-ui/prisma';
import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { ElementsSchema, PaginationSchema, withPaginationSchema } from '../common';

const c = initContract();
const pathPrefix = '/report';

const EditableReportSchema = ReportSchema.pick({ title: true, description: true }).partial();
const EditableReportItemSchema = ReportItemSchema.pick({ ordinal: true, payload: true }).partial();

export const reportContract = c.router(
  {
    validateScifinderToken: {
      method: 'GET',
      path: '/meta/validate-scifinder-token',
      headers: z.object({
        authorization: z.string(),
      }),
      responses: {
        200: z.object({ valid: z.boolean() }),
      },
    },
    fetchReport: {
      method: 'GET',
      path: '/:id',
      pathParams: z.object({
        id: z.string(),
      }),
      responses: {
        200: ReportSchema,
      },
    },
    fetchReports: {
      method: 'GET',
      path: '',
      query: PaginationSchema,
      responses: {
        200: z.object({
          pagination: z.object({
            total: z.number(),
          }),
          data: z.array(
            ReportSchema.extend({
              itemCount: z.number(),
            })
          ),
        }),
      },
    },
    fetchReportItems: {
      method: 'GET',
      path: '/:id/items',
      pathParams: z.object({
        id: z.string(),
      }),
      query: PaginationSchema,
      responses: {
        200: withPaginationSchema(z.array(ReportItemSchema)),
      },
    },
    fetchReportItemsNumber: {
      method: 'GET',
      path: '/:id/items-number',
      pathParams: z.object({
        id: z.string(),
      }),
      responses: {
        200: z.number(),
      },
    },
    createReport: {
      method: 'POST',
      path: '',
      body: z
        .object({
          title: z.string(),
        })
        .partial(),
      responses: {
        201: ReportSchema,
      },
    },
    updateReport: {
      method: 'PATCH',
      path: '/:id',
      pathParams: z.object({
        id: z.string(),
      }),
      body: EditableReportSchema,
      responses: {
        200: ReportSchema,
      },
    },
    removeReports: {
      method: 'DELETE',
      path: '',
      body: z.array(ReportSchema.shape.id),
      responses: {
        200: z.array(ReportSchema.shape.id),
      },
    },
    addReportItems: {
      method: 'POST',
      path: '/:reportId/item',
      pathParams: z.object({
        reportId: ReportSchema.shape.id,
      }),
      headers: z
        .object({
          authorization: z.string().optional(),
        })
        .optional(),
      body: z.object({
        elements: ElementsSchema,
        searchResultIds: z.array(z.string()),
        searchId: z.string(),
        includeScifinderRelatedData: z.boolean(),
      }),
      responses: {
        201: z.array(ReportItemSchema.shape.id),
      },
    },
    updateReportItem: {
      method: 'PATCH',
      path: '/:reportId/item/:itemId',
      pathParams: z.object({
        reportId: ReportSchema.shape.id,
        itemId: ReportItemSchema.shape.id,
      }),
      body: EditableReportItemSchema,
      responses: {
        200: ReportSchema,
      },
    },
    removeReportItems: {
      method: 'DELETE',
      path: '/:reportId/item',
      pathParams: z.object({
        reportId: ReportSchema.shape.id,
      }),
      body: z.array(ReportItemSchema.shape.id),
      responses: {
        200: ReportSchema,
      },
    },
    getPdfFile: {
      method: 'GET',
      path: '/pdf/:id',
      pathParams: z.object({
        id: z.string(),
      }),
      headers: z
        .object({
          'content-type': z.string(),
        })
        .partial(),
      responses: {
        200: z.any(),
      },
    },
  },
  {
    pathPrefix,
    validateResponseOnClient: true,
  }
);
